import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ShieldCheck, Stethoscope } from 'lucide-react';
import WorkspaceFrame from '../components/WorkspaceFrame';
import { useAuth } from '../utils/auth';
import { useAdminAuth } from '../utils/adminAuth';
import { useCapability } from '../utils/capabilityContext';
import './ModuleSelector.css';

const WorkspaceEntry = ({
  action,
  authenticated,
  description,
  Icon,
  loading,
  title,
  to,
  variant
}) => {
  const { t } = useTranslation();

  return (
    <Link
      to={to}
      className={`module-workspace-entry module-workspace-entry--${variant}`}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={(event) => {
        if (loading) {
          event.preventDefault();
        }
      }}
    >
      <span className="module-workspace-entry__icon" aria-hidden="true">
        <Icon size={variant === 'primary' ? 30 : 22} strokeWidth={1.8} />
      </span>

      <div className="module-workspace-entry__body">
        <div className="module-workspace-entry__heading">
          <h3 className="module-workspace-entry__title">{title}</h3>
          {authenticated && !loading && (
            <span className="module-workspace-entry__status">{t('dashboard.signedIn')}</span>
          )}
        </div>
        <p className="module-workspace-entry__description">{description}</p>
      </div>

      <span className="module-workspace-entry__action">
        <span>{loading ? t('common.loading') : action}</span>
        {!loading && <ArrowRight className="module-workspace-entry__arrow" size={18} aria-hidden="true" />}
      </span>
    </Link>
  );
};

const WorkspaceActions = ({ actions }) => (
  <div className="workspace-action-list">
    {actions.map((action) => (
      <Link key={action.to} to={action.to} className="workspace-action">
        <span className="workspace-action__copy">
          <span className="workspace-action__label">{action.label}</span>
          <span className="workspace-action__description">{action.description}</span>
        </span>
        <span className="workspace-action__arrow">→</span>
      </Link>
    ))}
  </div>
);

export const ModuleSelector = () => {
  const { t } = useTranslation();
  const { isAuthenticated, loading: doctorAuthLoading } = useAuth();
  const { isAdminAuthenticated, loading: adminAuthLoading } = useAdminAuth();

  return (
    <WorkspaceFrame>
      <main className="module-selector">
        <section className="module-selector__intro" aria-labelledby="workspace-selector-title">
          <span className="module-selector__eyebrow">{t('dashboard.moduleSelector')}</span>
          <h1 id="workspace-selector-title" className="module-selector__title">
            {t('dashboard.chooseWorkspace')}
          </h1>
        </section>

        <section className="module-selector__choices" aria-label={t('dashboard.workspaceSelectorLabel')}>
          <WorkspaceEntry
            action={isAuthenticated ? t('dashboard.enterDoctorWorkspace') : t('dashboard.doctorLogin')}
            authenticated={isAuthenticated}
            description={t('dashboard.doctorEntryDescription')}
            Icon={Stethoscope}
            loading={doctorAuthLoading}
            title={t('dashboard.doctorDesk')}
            to="/doctor"
            variant="primary"
          />

          <WorkspaceEntry
            action={isAdminAuthenticated ? t('dashboard.enterAdminWorkspace') : t('dashboard.adminLogin')}
            authenticated={isAdminAuthenticated}
            description={t('dashboard.adminEntryDescription')}
            Icon={ShieldCheck}
            loading={adminAuthLoading}
            title={t('dashboard.adminArea')}
            to="/admin"
            variant="secondary"
          />
        </section>
      </main>
    </WorkspaceFrame>
  );
};

export const DoctorHome = () => {
  const { currentUser } = useAuth();
  const { t } = useTranslation();

  const actions = [
    {
      label: t('dashboard.pendingReports'),
      description: t('dashboard.pendingReportsDescription'),
      to: '/reports'
    },
    {
      label: t('dashboard.reviewedReports'),
      description: t('dashboard.reviewedReportsDescription'),
      to: '/reviewed-reports'
    },
    {
      label: t('dashboard.appointmentOrders'),
      description: t('dashboard.appointmentOrdersDescription'),
      to: '/appointment-orders'
    },
    {
      label: t('dashboard.userCenter'),
      description: t('dashboard.userCenterDescription'),
      to: '/user-center'
    }
  ];

  return (
    <div className="stack">
      <div className="workspace-home-grid">
        <section className="surface-panel surface-panel--accent workspace-identity-panel">
          <div className="surface-panel__body stack">
            <div className="page-title-block">
              <h2 className="report-panel-title">{t('dashboard.currentAccount')}</h2>
            </div>
            <div className="info-list">
              <div className="info-list__row">
                <span className="info-list__label">{t('auth.name')}</span>
                <span className="info-list__value">{currentUser?.name || t('dashboard.notSet')}</span>
              </div>
              <div className="info-list__row">
                <span className="info-list__label">{t('dashboard.jobTitle')}</span>
                <span className="info-list__value">{currentUser?.title || t('dashboard.notSet')}</span>
              </div>
              <div className="info-list__row">
                <span className="info-list__label">{t('dashboard.specialty')}</span>
                <span className="info-list__value">{currentUser?.specialty || t('dashboard.notSet')}</span>
              </div>
              <div className="info-list__row">
                <span className="info-list__label">{t('userCenter.licenseNumber')}</span>
                <span className="info-list__value">{currentUser?.license_number || t('dashboard.notSet')}</span>
              </div>
            </div>
          </div>
        </section>

        <section className="surface-panel">
          <div className="surface-panel__body stack">
            <div className="page-title-block">
              <h2 className="report-panel-title">{t('dashboard.commonActions')}</h2>
            </div>
            <WorkspaceActions actions={actions} />
          </div>
        </section>
      </div>
    </div>
  );
};

export const AdminHome = () => {
  const { logoutAdmin } = useAdminAuth();
  const { refresh: refreshCapabilities } = useCapability();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const actions = [
    {
      label: t('dashboard.patientManagement'),
      description: t('dashboard.patientManagementDescription'),
      to: '/admin/patients'
    },
    {
      label: t('dashboard.doctorManagement'),
      description: t('dashboard.doctorManagementDescription'),
      to: '/doctors'
    },
    {
      label: t('dashboard.clinicManagement'),
      description: t('dashboard.clinicManagementDescription'),
      to: '/clinics'
    },
    {
      label: t('tokenUsage.menuLabel'),
      description: t('dashboard.tokenUsageDescription'),
      to: '/admin/token-usage'
    },
    {
      label: t('healthHouseStations.menuLabel'),
      description: t('dashboard.healthHouseStationsDescription'),
      to: '/health-house-stations'
    }
  ];

  const handleLogout = async () => {
    logoutAdmin();
    await refreshCapabilities();
    navigate('/admin-login');
  };

  return (
    <div className="admin-page">
      <div className="page-header admin-header-panel">
        <div className="page-title-block">
          <h1 className="page-title">{t('dashboard.adminHome')}</h1>
        </div>
        <div className="page-actions">
          <button type="button" className="theme-button theme-button--ghost" onClick={handleLogout}>
            {t('dashboard.logoutAdmin')}
          </button>
        </div>
      </div>

      <div className="workspace-home-grid workspace-home-grid--single">
        <section className="surface-panel admin-panel">
          <div className="surface-panel__body stack">
            <div className="page-title-block">
              <h2 className="report-panel-title">{t('dashboard.commonActions')}</h2>
            </div>
            <WorkspaceActions actions={actions} />
          </div>
        </section>
      </div>
    </div>
  );
};

export default ModuleSelector;
